import React, { useState } from 'react';
import { 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Paper,
  Container,
  InputAdornment,
  IconButton,
  Alert,
  Link,
  alpha,
  LinearProgress
} from '@mui/material';
import { 
  Person as PersonIcon,
  Email as EmailIcon, 
  Lock as LockIcon, 
  Visibility, 
  VisibilityOff,
  PersonAdd as PersonAddIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { register } from '../services/api';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

// Paleta profesional verde-azul
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  error: '#d63031',
  warning: '#eb9800ff',
  dark: '#2d3436',
  light: '#dfe6e9'
};

// Validación de contraseña
const getPasswordStrength = (password) => {
  let strength = 0;
  if (password.length >= 8) strength += 25;
  if (password.match(/[a-z]/)) strength += 25;
  if (password.match(/[A-Z]/)) strength += 25;
  if (password.match(/[0-9]/) || password.match(/[^a-zA-Z0-9]/)) strength += 25;
  return strength;
};

const getPasswordColor = (strength) => {
  if (strength <= 25) return COLORS.error;
  if (strength <= 50) return COLORS.warning;
  if (strength <= 75) return COLORS.primary;
  return COLORS.success;
};

const getPasswordLabel = (strength) => {
  if (strength <= 25) return 'Débil';
  if (strength <= 50) return 'Regular';
  if (strength <= 75) return 'Buena';
  return 'Fuerte';
};

const Register = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const passwordStrength = getPasswordStrength(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await register({ username, email, password });
      alert(response.data.message);
      navigate('/login');
    } catch (error) {
      setError(error.response?.data?.message || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${alpha(COLORS.gradient1, 0.1)} 0%, ${alpha(COLORS.gradient2, 0.1)} 100%)`,
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            border: '2px solid #e0e0e0'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
              p: 4,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Círculos decorativos */}
            <Box sx={{ position: 'absolute', top: -30, right: -30, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
            <Box sx={{ position: 'absolute', bottom: -20, left: '30%', width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
            
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}
            >
              <PersonAddIcon sx={{ fontSize: 44, color: 'white' }} />
            </Box>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 800, mb: 1 }}>
              Crear Cuenta
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.85)' }}>
              Únete a nuestra plataforma de formación
            </Typography>
          </Box>

          {/* Formulario */}
          <Box sx={{ p: 4 }}>
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3, 
                  borderRadius: 2,
                  border: `1px solid ${alpha(COLORS.error, 0.3)}`
                }}
              >
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                label="Nombre de usuario"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                fullWidth
                required
                placeholder="Tu nombre"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: COLORS.primary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: COLORS.primary },
                    '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                  }
                }}
              />

              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                placeholder="tu@email.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: COLORS.primary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: COLORS.primary },
                    '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                  }
                }}
              />

              <TextField
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
                placeholder="Mínimo 8 caracteres"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: COLORS.primary }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#9e9e9e' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: COLORS.primary },
                    '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                  }
                }}
              />

              {/* Indicador de fuerza de contraseña */}
              {password && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#636e72' }}>
                      Seguridad de la contraseña
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: getPasswordColor(passwordStrength), 
                        fontWeight: 600 
                      }}
                    >
                      {getPasswordLabel(passwordStrength)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={passwordStrength}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: alpha(getPasswordColor(passwordStrength), 0.2),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: getPasswordColor(passwordStrength),
                        borderRadius: 3
                      }
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                    {[
                      { check: password.length >= 8, label: '8+ caracteres' },
                      { check: /[a-z]/.test(password), label: 'Minúscula' },
                      { check: /[A-Z]/.test(password), label: 'Mayúscula' },
                      { check: /[0-9]/.test(password) || /[^a-zA-Z0-9]/.test(password), label: 'Número/Símbolo' }
                    ].map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircleIcon 
                          sx={{ 
                            fontSize: 14, 
                            color: item.check ? COLORS.success : '#bdbdbd' 
                          }} 
                        />
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: item.check ? COLORS.success : '#9e9e9e' 
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading || passwordStrength < 50}
                startIcon={<PersonAddIcon />}
                sx={{
                  py: 1.5,
                  background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                  fontWeight: 700,
                  fontSize: '1rem',
                  borderRadius: 2,
                  boxShadow: `0 4px 14px ${alpha(COLORS.gradient1, 0.4)}`,
                  '&:hover': {
                    boxShadow: `0 6px 20px ${alpha(COLORS.gradient1, 0.5)}`
                  },
                  '&.Mui-disabled': {
                    background: '#e0e0e0'
                  }
                }}
              >
                {loading ? 'Registrando...' : 'Crear Cuenta'}
              </Button>
            </form>

            <Box sx={{ textAlign: 'center', mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
              <Typography sx={{ color: '#636e72' }}>
                ¿Ya tienes una cuenta?{' '}
                <Link
                  component={RouterLink}
                  to="/login"
                  sx={{
                    color: COLORS.primary,
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  Inicia sesión
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Footer */}
        <Typography 
          variant="body2" 
          sx={{ 
            textAlign: 'center', 
            mt: 3, 
            color: '#9e9e9e' 
          }}
        >
          © {new Date().getFullYear()} Plataforma de Formación
        </Typography>
      </Container>
    </Box>
  );
};

export default Register;